import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Input, Card, FormGroup, PageHeader, Button, Checkbox } from "@cofano/cck";

import { CircularProgress, Grid2 as Grid, Tabs, Tab, Tooltip, Box } from "@mui/material";
import Info from "@mui/icons-material/Info";

import DatePicker from "components/cck/DatePicker";
import Helmet from "components/helmet/Helmet";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import RelationSelect from "view/relations/components/selects/RelationSelect";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import ShipSelect from "view/ships/components/selects/ShipSelect";
import SailingScheduleSelect from "view/sailingschedules/components/selects/SailingScheduleSelect";

import { Language, hasOptionalFieldPermission, Fields, hasPluginPermission } from "imports/Utils";

import { VoyageTabs } from "view/voyages/utils/VoyageUtils";

import VoyagePurchaseChartersForm from "./components/form/VoyagePurchaseChartersForm";
import VoyageCallsForm from "./components/form/VoyageCallsForm";
import { voyageSchema, VoyageSchemaInput, VoyageSchemaOutput } from "./utils/voyage.schema";

import { LocationType } from "__generated__/enums";
import { GetVoyageDocument, GetVoyageQuery } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { SaveVoyageDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";
import { ChildProps } from "types/commons";

const VoyageCreateEdit = () => {

	const params = useParams();

	const id = params.id;

	const navigate = useNavigate();
	const session = useStoreState(store => store.session);

	const [ selectedTab, selectTab ] = useState(VoyageTabs.Details);
	const [ voyage, setVoyage ] = useState<ChildProps<GetVoyageQuery, "voyage"> | null>(null);

	const form = useForm<VoyageSchemaInput, unknown, VoyageSchemaOutput>({
		resolver: zodResolver(voyageSchema),
		defaultValues: {
			id: null,
			voyageNumber: "",
			ship: null,
			creditor: null,
			start: null,
			end: null,
			schedule: null,
			isBallast: false,
			scopeIn: {
				desired: null,
				planned: null,
				actual: null,
				expected: null,
				expectedLocation: null,
				plannedLocation: null,
				actualLocation: null,
			},
			scopeOut: {
				desired: null,
				planned: null,
				actual: null,
				expected: null,
				expectedLocation: null,
				plannedLocation: null,
				actualLocation: null,
			},
			calls: [],
			purchaseCharters: null,
		},
	});

	const { handleSubmit, watch, reset } = form;

	const [ ship, start, end ] = watch([ "ship", "start", "end" ]);

	const [ getVoyage, { loading: getVoyageLoading } ] = useLazyQuery(GetVoyageDocument, {
		onCompleted: (response) => {
			setVoyage(response.voyage ?? null);

			reset({
				id: response.voyage?.id ?? null,
				voyageNumber: response.voyage?.voyageNumber ?? "",
				ship: response.voyage?.ship ?? null,
				creditor: response.voyage?.creditor ?? null,
				start: response.voyage?.start ?? null,
				end: response.voyage?.end ?? null,
				schedule: response.voyage?.schedule ?? null,
				isBallast: response.voyage?.isBallast ?? false,
				scopeIn: {
					desired: response.voyage?.scopeIn?.desired ?? null,
					planned: response.voyage?.scopeIn?.planned ?? null,
					actual: response.voyage?.scopeIn?.actual ?? null,
					expected: response.voyage?.scopeIn?.expected ?? null,
					expectedLocation: response.voyage?.scopeIn?.expectedLocation ?? null,
					plannedLocation: response.voyage?.scopeIn?.plannedLocation ?? null,
					actualLocation: response.voyage?.scopeIn?.actualLocation ?? null,
				},
				scopeOut: {
					desired: response.voyage?.scopeOut?.desired ?? null,
					planned: response.voyage?.scopeOut?.planned ?? null,
					actual: response.voyage?.scopeOut?.actual ?? null,
					expected: response.voyage?.scopeOut?.expected ?? null,
					expectedLocation: response.voyage?.scopeOut?.expectedLocation ?? null,
					plannedLocation: response.voyage?.scopeOut?.plannedLocation ?? null,
					actualLocation: response.voyage?.scopeOut?.actualLocation ?? null,
				},
				calls: response.voyage?.calls?.map(call => ({
					id: call.id,
					location: call.location,
					atn: call.atn ?? null,
					dtn: call.dtn ?? null,
					ata: call.ata ?? null,
					atd: call.atd ?? null,
					ateo: call.ateo ?? null,
					atso: call.atso ?? null,
					eta: call.eta ?? null,
					etd: call.etd ?? null,
					eteo: call.eteo ?? null,
					etso: call.etso ?? null,
					pta: call.pta ?? null,
					ptd: call.ptd ?? null,
					expectedCargos: call.expectedCargos ?? [],
				})) ?? [],
				purchaseCharters: response.voyage?.purchaseCharters?.map(purchaseCharter => ({
					id: purchaseCharter.id,
					type: purchaseCharter.type,
					subtype: purchaseCharter.subtype,
					charterNumber: purchaseCharter.charterNumber,
					creditor: null,
					debtor: null,
					validFrom: null,
					validUntil: null,
					shortNotice: false,
				})) ?? null,
			});
		},
	});

	const [ createOrEditVoyage, { loading } ] = useMutation(SaveVoyageDocument);

	const canAddCharters = !hasPluginPermission(session, "externalVoyageCallsManagement") && !!ship && !!start && !!end;

	const save = (data: VoyageSchemaOutput) => {
		createOrEditVoyage({
			variables: {
				input: {
					id: data.id,
					voyageNumber: data.voyageNumber  || undefined,
					shipId: data.ship.id,
					creditor: data.creditor ? { id: data.creditor.id } : undefined,
					schedule: data.schedule,
					isBallast: data.isBallast,
					scopeIn: {
						desired: data.scopeIn.desired,
						planned: data.scopeIn.planned,
						actual: data.scopeIn.actual,
						expected: data.scopeIn.expected,
						expectedLocationId: data.scopeIn.expectedLocation?.id,
						plannedLocationId: data.scopeIn.plannedLocation?.id,
						actualLocationId: data.scopeIn.actualLocation?.id,
					},
					scopeOut: {
						desired: data.scopeOut.desired,
						planned: data.scopeOut.planned,
						actual: data.scopeOut.actual,
						expected: data.scopeOut.expected,
						expectedLocationId: data.scopeOut.expectedLocation?.id,
						plannedLocationId: data.scopeOut.plannedLocation?.id,
						actualLocationId: data.scopeOut.actualLocation?.id,
					},
					calls: data.calls.map(call => ({
						id: call.id,
						locationId: call.location.id,
						pta: call.pta,
						ptd: call.ptd,
						ata: call.ata,
						atd: call.atd,
						eta: call.eta,
						etd: call.etd,
						atso: call.atso,
						etso: call.etso,
						ateo: call.ateo,
						eteo: call.eteo,
						atn: call.atn,
						dtn: call.dtn,
						voyageId: data.id,
						expectedCargos: call.expectedCargos.map(expectedCargo => ({
							...expectedCargo,
							amount: Number(expectedCargo.amount),
							dischargingLocationId: expectedCargo.dischargingLocation?.id,
							callId: call.id,
							containerTypeCode: expectedCargo.containerType?.isoTypeCode ?? null,
							containerType: undefined,
							dischargingLocation: undefined,
						})),
					})),
					purchaseCharters: data.purchaseCharters ?? [],
				},
			},
		}).then((response) => {
			navigate(`/view/voyages/${response?.data?.voyageCreateOrEdit.id}/details`);
		});
	};

	useEffect(() => {
		if (id) {
			getVoyage({
				variables: {
					id: Number(id),
				},
			});
		}
	}, []);

	return (
		<div>
			<FormProvider {...form}>
				<form onSubmit={handleSubmit(save)}>
					<PageHeader>
						<PageHeader.Title>
							{Language("VOYAGE")}
							<Helmet title={intl.formatMessage({ id: "VOYAGE" })} />
						</PageHeader.Title>

						<PageHeader.Actions>
							<Button
								onClick={() => navigate(id ? `/view/voyages/${id}/details` : "/view/voyages")}
								variant="outlined">
								{Language("CANCEL")}
							</Button>

							<Button loading={loading} type="submit">
								{Language("SAVE")}
							</Button>
						</PageHeader.Actions>
					</PageHeader>

					{getVoyageLoading ? (
						<CircularProgress size={24} />
					) : (
						<React.Fragment>
							<Box mb={2}>
								<Card disablePadding>
									<Tabs
										value={selectedTab}
										indicatorColor="primary"
										onChange={(_, value) => selectTab(value)}
										variant="scrollable">
										<Tab
											label={`${intl.formatMessage({ id: "DETAILS" })} & ${intl.formatMessage({ id: "CALLS" })}`}
											value={VoyageTabs.Details} />

										<Tab
											style={{ pointerEvents: "auto" }}
											disabled={!canAddCharters}
											label={(
												<Tooltip title={!canAddCharters ? intl.formatMessage({ id: "SELECT_SHIP_AND_VOYAGE_WINDOW" }) : ""}>
													<span>
														{intl.formatMessage({ id: "CHARTERS" })}
													</span>
												</Tooltip>
											)}
											value={VoyageTabs.Charters} />

										{((hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn")) || hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut")) && (
											<Tab label={Language("VOYAGE_SCOPE")} value={VoyageTabs.Scope} />
										)}
									</Tabs>
								</Card>
							</Box>

							{selectedTab === VoyageTabs.Details && (
								<Grid spacing={3} container>
									<Grid size={{ lg: 4, md: 4, xs: 12 }}>
										<VoyageDetails />
									</Grid>

									<Grid size={{ lg: 8, md: 8, xs: 12 }}>
										<VoyageCallsForm voyage={voyage} />
									</Grid>
								</Grid>
							)}

							{selectedTab === VoyageTabs.Charters && (
								<VoyagePurchaseChartersForm />
							)}

							{selectedTab === VoyageTabs.Scope && (
								<Scope />
							)}
						</React.Fragment>
					)}
				</form>
			</FormProvider>
		</div>
	);
};

const VoyageDetails = () => {

	const { control, setValue } = useFormContext<VoyageSchemaInput, unknown, VoyageSchemaOutput>();

	const session = useStoreState(store => store.session);

	const id = useWatch({ control, name: "id" });
	const calls = useWatch({ control, name: "calls" });

	const hasExpectedCargos = calls.some(call => call.expectedCargos.length > 0);

	return (
		<Card title={Language("VOYAGE")} icon={<Info />} collapsable>
			<FormGroup
				label={Language("VOYAGE_NUMBER")}
				labelPlacement="left-center"
				tooltip={intl.formatMessage({ id: "VOYAGE_NUMBER_TIPS" })}
				overflow="hidden">
				<Controller
					name="voyageNumber"
					control={control}
					render={({ field }) => <Input {...field} autoFocus={!id} disabled={!!id} />} />
			</FormGroup>

			<FormGroup
				label={Language("SHIP")}
				labelPlacement="left-center"
				overflow="hidden"
				required>
				<Controller
					name="ship"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<ShipSelect
								{...field}
								onChange={(option) => {
									field.onChange(option);
									setValue("creditor", option?.owner ?? null);
									setValue("schedule", option?.sailingSchedule ?? null);
									setValue("purchaseCharters", null);
								}} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup label={Language("CREDITOR")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="creditor"
					control={control}
					render={({ field }) => <RelationSelect {...field} disabled={true} />} />
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.VOYAGE, "schedule") && (
				<FormGroup label={Language("VOYAGE_SCHEDULE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="schedule"
						control={control}
						render={({ field }) => <SailingScheduleSelect {...field} />} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.VOYAGE, "isBallast") && (
				<FormGroup
					label={Language("IS_BALLAST")}
					tooltip={hasExpectedCargos ? intl.formatMessage({ id: "BALLAST_TRIP_CANNOT_HAVE_EXPECTED_CARGOS" }) : undefined}
					labelPlacement="left-center"
					overflow="hidden">
					<Controller
						name="isBallast"
						control={control}
						disabled={hasExpectedCargos}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}
		</Card>
	);
};

const Scope = () => {

	const { control } = useFormContext<VoyageSchemaInput, unknown, VoyageSchemaOutput>();

	const session = useStoreState(store => store.session);

	return (
		<Grid spacing={3} container>
			{(hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn")) && (
				<Grid size={{ lg: 6, md: 12, xs: 12 }}>
					<Card icon={<Info />} title={intl.formatMessage({ id: "ROTATION_SCOPE_IN" })}>
						<FormGroup label={Language("ROTATION_SCOPE_IN_DESIRED_ARRIVAL")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeIn.desired"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup label={Language("ROTATION_SCOPE_IN_PLANNED_ARRIVAL")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeIn.planned"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup label={Language("ROTATION_SCOPE_IN_EXPECTED_ARRIVAL")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeIn.expected"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup label={Language("ROTATION_SCOPE_IN_ACTUAL_ARRIVAL")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeIn.actual"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_EXPECTED")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeIn.expectedLocation"
								control={control}
								render={({ field }) => <LocationSelect {...field} types={[ LocationType.ScopeIn ]} />} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_PLANNED")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeIn.plannedLocation"
								control={control}
								render={({ field }) => <LocationSelect {...field} types={[ LocationType.ScopeIn ]} />} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_ACTUAL")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeIn.actualLocation"
								control={control}
								render={({ field }) => <LocationSelect {...field} types={[ LocationType.ScopeIn ]} />} />
						</FormGroup>
					</Card>
				</Grid>
			)}

			{(hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut")) && (
				<Grid size={{ lg: 6, md: 12, xs: 12 }}>
					<Card icon={<Info />} title={intl.formatMessage({ id: "ROTATION_SCOPE_OUT" })}>
						<FormGroup label={Language("ROTATION_SCOPE_OUT_DESIRED_DEPARTURE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeOut.desired"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup label={Language("ROTATION_SCOPE_OUT_PLANNED_DEPARTURE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeOut.planned"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup label={Language("ROTATION_SCOPE_OUT_EXPECTED_DEPARTURE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeOut.expected"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup label={Language("ROTATION_SCOPE_OUT_ACTUAL_DEPARTURE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeOut.actual"
								control={control}
								render={({ field }) => <DatePicker {...field} showTimeInput />} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_EXPECTED")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeOut.expectedLocation"
								control={control}
								render={({ field }) => <LocationSelect {...field} types={[ LocationType.ScopeOut ]} />} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_PLANNED")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeOut.plannedLocation"
								control={control}
								render={({ field }) => <LocationSelect {...field } types={[ LocationType.ScopeOut ]} />} />
						</FormGroup>

						<FormGroup label={Language("VOYAGE_SCOPE_LOCATION_ACTUAL")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="scopeOut.actualLocation"
								control={control}
								render={({ field }) => <LocationSelect {...field} types={[ LocationType.ScopeOut ]} />} />
						</FormGroup>
					</Card>
				</Grid>
			)}
		</Grid>
	);
};

export default VoyageCreateEdit;
